import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Col, Dropdown, Form, Modal, Row, Spinner } from "react-bootstrap";

import Layout from "../../../layout";
import MISButton from "../../../../components/button";
import { MatchForExtractState } from "../../../../models/DataMatchContext";
import { MisType } from "../../../../app/enums";
import {
    extractIncomingColumns,
    matchColumnPlaceholderLabel,
    matchColumnPlaceholderValue,
    misCentralColumns,
    type ColumnOption,
} from "./column-options";
import { centralRowKey, providerRowKey } from "./data-match-utils";
import { DATA_MATCH_STATE_KEY, formatSnapshotDisplayName } from "./data-match-helpers";
import { useDataMatch } from "./useDataMatch";
import MatchedRecordsSection from "./MatchedRecordsSection";
import UnmatchedCentralPanel from "./UnmatchedCentralPanel";
import UnmatchedProviderPanel from "./UnmatchedProviderPanel";

// ─── Types ──────────────────────────────────────────────────────────────────────

type LocationState = { matchForExtract?: MatchForExtractState };

// ─── Page wrapper ───────────────────────────────────────────────────────────────

export default function DataMatchPage() {
    const navigate = useNavigate();
    const { establishmentId } = useParams();
    const location = useLocation();
    const state = (location.state as LocationState | null) ?? null;

    const match = useMemo(() => {
        if (state?.matchForExtract) {
            try {
                sessionStorage.setItem(DATA_MATCH_STATE_KEY, JSON.stringify(state.matchForExtract));
            } catch { /* ignore */ }
            return state.matchForExtract;
        }
        try {
            const stored = sessionStorage.getItem(DATA_MATCH_STATE_KEY);
            if (stored) {
                return JSON.parse(stored) as MatchForExtractState;
            }
        } catch { /* ignore */ }
        return undefined;
    }, [state]);

    const backToExtractClients = () => {
        sessionStorage.removeItem(DATA_MATCH_STATE_KEY);
        navigate(`/establishment/${establishmentId}/extract-clients`, { replace: true });
    };

    if (!match || String(match.establishmentId) !== String(establishmentId)) {
        return (
            <Layout
                title="Data Match"
                backArrow={<FontAwesomeIcon icon={faArrowLeft} onClick={backToExtractClients} className="me-4 fs-5 cursor-pointer" />}
            >
                <p className="mt-3">No extract was selected for Data Match, or this page was opened directly.</p>
                <MISButton text="Back to Extract Client Setup" className="btn btn-primary crbc-bg-color mt-2" FnOnClick={backToExtractClients} />
            </Layout>
        );
    }

    return <DataMatchContent match={match} establishmentId={String(establishmentId)} onBack={backToExtractClients} />;
}

// ─── Content ────────────────────────────────────────────────────────────────────

function DataMatchContent({
    match,
    establishmentId,
    onBack,
}: {
    match: MatchForExtractState;
    establishmentId: string;
    onBack: () => void;
}) {
    const providerName = match.providerLabel || MisType[match.misType]?.toUpperCase() || "Provider";
    const providerColumns = extractIncomingColumns;

    const dm = useDataMatch({ match, establishmentId, onBack });

    return (
        <Layout
            title="Data Match"
            backArrow={<FontAwesomeIcon icon={faArrowLeft} onClick={onBack} className="me-4 fs-5 cursor-pointer" />}
        >
            <div id="dataMatchPage">
                <p className="text-muted small mb-3">
                    Incoming extract: <strong>{providerName}</strong> · MIS configuration {match.misConfigurationID}
                </p>

                {/* ── Match filters card ── */}
                <div className="match-filters-card">
                    <div className="match-filters-title">Match Filters</div>

                    {dm.matchRows.map((row) => (
                        <Row key={row.id} className="g-2 mb-3 align-items-stretch">
                            <Col xs={12} md={3}>
                                <Form.Label className="small text-muted mb-1">MIS Central column</Form.Label>
                                <MatchFilterColumnDropdown
                                    idPrefix={`dm-central-${row.id}`}
                                    columns={misCentralColumns}
                                    value={row.centralColumn}
                                    onSelect={(v) => dm.updateRow(row.id, { centralColumn: v })}
                                    isOpen={dm.openDropdownId === `dm-central-${row.id}`}
                                    onToggle={(show) => dm.setOpenDropdownId(show ? `dm-central-${row.id}` : null)}
                                />
                            </Col>
                            <Col xs={12} md="auto" className="match-filter-equals-col">
                                <div className="match-filter-equals-spacer" aria-hidden="true" />
                                <div className="match-filter-equals-cell">
                                    <span className="match-filter-equals">=</span>
                                </div>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Label className="small text-muted mb-1">{providerName} column</Form.Label>
                                <MatchFilterColumnDropdown
                                    idPrefix={`dm-provider-${row.id}`}
                                    columns={providerColumns}
                                    value={row.providerColumn}
                                    onSelect={(v) => dm.updateRow(row.id, { providerColumn: v })}
                                    isOpen={dm.openDropdownId === `dm-provider-${row.id}`}
                                    onToggle={(show) => dm.setOpenDropdownId(show ? `dm-provider-${row.id}` : null)}
                                />
                            </Col>
                            <Col xs={12} md={4}>
                                <Form.Label className="small text-muted mb-1">
                                    <span>Similarity threshold</span>{" "}
                                    <span className="match-score-form-label__pct">{row.matchScore}%</span>
                                </Form.Label>
                                <div className="data-match-score-control-row">
                                    <div className="data-match-score-slider">
                                        <div
                                            className="data-match-score-slider__fill"
                                            style={{ width: `${row.matchScore}%` }}
                                            aria-hidden="true"
                                        />
                                        <input
                                            type="range"
                                            className="data-match-score-slider__input"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={row.matchScore}
                                            aria-label={`Similarity threshold for this column pair, ${row.matchScore} percent. 100 means exact match; lower values allow fuzzy matches.`}
                                            onChange={(e) =>
                                                dm.updateRow(row.id, {
                                                    matchScore: Math.min(100, Math.max(0, Number(e.target.value))),
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} md="auto" className="match-filter-actions-col">
                                <div className="match-filter-equals-spacer" aria-hidden="true" />
                                <div className="match-filter-actions-cell">
                                    {dm.matchRows.length > 1 ? (
                                        <button type="button" className="btn btn-link btn-sm text-danger p-0 text-decoration-none" onClick={() => dm.removeRow(row.id)}>
                                            Remove
                                        </button>
                                    ) : (
                                        <span className="small text-muted">&nbsp;</span>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    ))}

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2 pt-2 border-top">
                        <MISButton
                            text="Add additional matching columns"
                            className="btn btn-outline-secondary"
                            FnOnClick={dm.addRow}
                            disabled={dm.matchRunning}
                        />
                        <MISButton
                            text={dm.matchRunning ? "Running…" : "Perform data match"}
                            className="btn btn-primary crbc-bg-color"
                            FnOnClick={dm.performDataMatch}
                            disabled={dm.matchRunning || dm.hasManualLinks}
                        />
                    </div>
                </div>

                {/* ── Unmatched panels ── */}
                <Row className="g-3 mb-3 data-match-unmatched-row">
                    <Col md={6} className="data-match-unmatched-col">
                        {dm.previewLoading ? (
                            <div className="data-match-panel data-match-panel--central">
                                <div className="data-match-panel__head">
                                    <span>MIS Central (left) | Unmatched (…)</span>
                                </div>
                                <div className="data-match-panel__body p-0">
                                    <div className="p-4 d-flex justify-content-center align-items-center gap-2 text-muted small">
                                        <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                                        <span>Loading central records…</span>
                                    </div>
                                </div>
                            </div>
                        ) : dm.runResult ? (
                            <UnmatchedCentralPanel
                                rows={dm.runResult.unmatchedCentral}
                                selectedRowKey={dm.selectedCentral ? centralRowKey(dm.selectedCentral) : null}
                                onRowSelect={dm.handleSelectCentral}
                            />
                        ) : (
                            <div className="data-match-panel data-match-panel--central">
                                <div className="data-match-panel__head">
                                    <span>MIS Central (left) | Unmatched (—)</span>
                                </div>
                                <div className="data-match-panel__body p-0">
                                    <p className="p-3 mb-0 text-muted small">Could not load central records.</p>
                                </div>
                            </div>
                        )}
                    </Col>
                    <Col md={6} className="data-match-unmatched-col">
                        {dm.previewLoading ? (
                            <div className="data-match-panel data-match-panel--provider">
                                <div className="data-match-panel__head">
                                    <span>{providerName} (right) | Unmatched (…)</span>
                                </div>
                                <div className="data-match-panel__body p-0">
                                    <div className="p-4 d-flex justify-content-center align-items-center gap-2 text-muted small">
                                        <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                                        <span>Loading provider records…</span>
                                    </div>
                                </div>
                            </div>
                        ) : dm.runResult ? (
                            <UnmatchedProviderPanel
                                rows={dm.runResult.unmatchedProvider}
                                providerName={providerName}
                                selectedRowKey={dm.selectedProvider ? providerRowKey(dm.selectedProvider) : null}
                                onRowSelect={dm.handleSelectProvider}
                            />
                        ) : (
                            <div className="data-match-panel data-match-panel--provider">
                                <div className="data-match-panel__head">
                                    <span>{providerName} (right) | Unmatched (—)</span>
                                </div>
                                <div className="data-match-panel__body p-0">
                                    <p className="p-3 mb-0 text-muted small">Could not load provider records.</p>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>

                {/* ── Matched records ── */}
                {dm.runResult && dm.runResult.matchedPairs.length > 0 ? (
                    <MatchedRecordsSection
                        key={dm.dataMatchRunKey}
                        matchedCount={dm.runResult.matchedCount}
                        pairs={dm.runResult.matchedPairs}
                        providerName={providerName}
                        onUnmatch={dm.unmatchPairAt}
                        onUndo={dm.undoLastMatch}
                        canUndo={dm.canUndoLastMatch}
                    />
                ) : (
                    <div className="data-match-panel data-match-panel--matched">
                        <div className="data-match-panel__head border-start-0">
                            <span>
                                Matched records (
                                {dm.runResult
                                    ? dm.hasCompletedMatchAttempt
                                        ? `${dm.runResult.matchedCount} in this run`
                                        : "—"
                                    : "—"}
                                )
                            </span>
                        </div>
                        <div className="data-match-panel__body p-0">
                            <p className="p-3 mb-0 text-muted small">
                                {!dm.runResult
                                    ? "Match results are not available."
                                    : dm.hasCompletedMatchAttempt
                                        ? "No one-to-one matches in this run (try adjusting rules or thresholds)."
                                        : "Run a data match to see suggested matches here. Review the unmatched lists above to choose column rules and thresholds."}
                            </p>
                        </div>
                    </div>
                )}

                {/* ── Manual link confirmation modal ── */}
                <Modal show={dm.showManualLinkModal} onHide={dm.handleDismissManualLinkModal} centered backdrop="static">
                    <Modal.Header closeButton>
                        <Modal.Title>Link records</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {dm.selectedCentral && dm.selectedProvider ? (
                            <p className="mb-0">
                                Do you want to link <strong>{formatSnapshotDisplayName(dm.selectedCentral)}</strong> (MIS Central) with{" "}
                                <strong>{formatSnapshotDisplayName(dm.selectedProvider)}</strong> ({providerName})?
                            </p>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-end">
                        <Button variant="secondary" type="button" className="data-match-cancel-btn" onClick={dm.handleDismissManualLinkModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="button" className="data-match-link-btn crbc-bg-color" onClick={dm.handleConfirmManualLink}>
                            Link
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ── Footer actions ── */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-4 pt-3 border-top data-match-footer-actions">
                    <MISButton
                        text="Cancel"
                        className="btn btn-outline-secondary"
                        FnOnClick={() => dm.setAbandonModalOpen(true)}
                        disabled={dm.completeSubmitting}
                    />
                    {dm.runResult && dm.canEditEstablishments ? (
                        <MISButton
                            text={dm.completeSubmitting ? "Saving…" : "Complete"}
                            className="btn btn-success"
                            disabled={dm.completeSubmitting}
                            FnOnClick={() => dm.setCompleteModalOpen(true)}
                        />
                    ) : null}
                </div>

                {/* ── Complete confirmation modal ── */}
                <Modal show={dm.completeModalOpen} onHide={() => { if (!dm.completeSubmitting) dm.setCompleteModalOpen(false); }} centered backdrop="static">
                    <Modal.Header closeButton={!dm.completeSubmitting}>
                        <Modal.Title>Complete Data Match?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {dm.runResult ? (
                            <>
                                <p className="mb-3">
                                    You will return to Extract Client Setup. In production, matches and the related updates from this screen would be saved.
                                </p>
                                <ul className="mb-3 ps-3">
                                    <li className="mb-2">
                                        <strong>{dm.runResult.matchedPairs.length}</strong> matched records
                                    </li>
                                    <li className="mb-2">
                                        <strong>{dm.runResult.unmatchedCentral.length}</strong> unmatched records will be made leavers (unmatched MIS Central records)
                                    </li>
                                    <li className="mb-0">
                                        <strong>{dm.runResult.unmatchedProvider.length}</strong> unmatched records (in {providerName} but not MIS Central) will be
                                        created as new records
                                    </li>
                                </ul>
                                <p className="text-muted small mb-0">Cancel closes this dialog and keeps you on Data Match.</p>
                            </>
                        ) : (
                            <p className="mb-0">No match results are loaded.</p>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-end gap-2">
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => dm.setCompleteModalOpen(false)}
                            disabled={dm.completeSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            type="button"
                            onClick={dm.handleConfirmComplete}
                            disabled={dm.completeSubmitting || !dm.runResult}
                        >
                            {dm.completeSubmitting ? "Saving…" : "Confirm"}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ── Abandon confirmation modal ── */}
                <Modal show={dm.abandonModalOpen} onHide={() => dm.setAbandonModalOpen(false)} centered backdrop="static">
                    <Modal.Header closeButton>
                        <Modal.Title>Return without saving?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="mb-0">
                            Your data match progress on this screen will be lost. Confirm to return to Extract Client Setup without saving matches. Cancel stays on
                            Data Match.
                        </p>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" type="button" onClick={() => dm.setAbandonModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="warning"
                            type="button"
                            onClick={() => {
                                dm.setAbandonModalOpen(false);
                                onBack();
                            }}
                        >
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Layout>
    );
}

// ─── MatchFilterColumnDropdown ──────────────────────────────────────────────────

function MatchFilterColumnDropdown({
    idPrefix,
    columns,
    value,
    onSelect,
    isOpen,
    onToggle,
}: {
    idPrefix: string;
    columns: ColumnOption[];
    value: string;
    onSelect: (v: string) => void;
    isOpen: boolean;
    onToggle: (nextShow: boolean) => void;
}) {
    const normalized = value?.trim() ?? "";
    const selected = columns.find((c) => c.value === normalized);
    const showPlaceholder = !normalized || normalized === matchColumnPlaceholderValue;
    const label = showPlaceholder ? matchColumnPlaceholderLabel : selected?.label ?? normalized;

    return (
        <Dropdown className="data-match-match-filter-dropdown w-100" show={isOpen} onToggle={onToggle}>
            <Dropdown.Toggle
                id={`${idPrefix}-toggle`}
                variant="outline-secondary"
                className="dropdown-toggle data-match-match-filter-dropdown__toggle w-100 text-start d-flex justify-content-between align-items-center"
            >
                <span className="text-truncate">{label}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="data-match-column-picker-menu shadow-sm">
                <Dropdown.Item active={showPlaceholder} onClick={() => { onSelect(matchColumnPlaceholderValue); onToggle(false); }}>
                    {matchColumnPlaceholderLabel}
                </Dropdown.Item>
                {columns.map((c) => (
                    <Dropdown.Item key={c.value} active={normalized === c.value} onClick={() => { onSelect(c.value); onToggle(false); }}>
                        {c.label}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}
